import { toProviderIdentifier } from './helpers';
import { kmm } from './kmm';
import { ContentListProviders } from '@smart-tv/typings/content';
import {
  ContentInfoEpisodes,
  KtList,
  type Cast,
  type LastWatched,
  type NpvrInfo,
  type Version,
  type VersionProvider
} from '@norlys-a-s/norlys-play-kmm';

export interface ContentInfoEpisodesRaw {
  episodes: ContentInfoEpisodeRaw[];
  totalEpisodes: number;
}
export interface ContentInfoEpisodeRaw {
  providers?: {
    providerType: string;
    providerName: string;
    length: number;
    covers?: {
      current?: string;
    };
    hasTrailer: boolean;
    rentalPeriod: number;
    unPublishDate: number;
    access: boolean;
    previewDuration?: number;
    trailerMediaId?: number;
    lastWatched?: {
      persistantTimeIndex: number;
      highestTimeIndex: number;
      lastUpdated: number;
      secondsWatched: number;
      programLength: number;
      progressBar?: string;
      mediaId?: number;
      eventId?: number;
      sid?: number;
    };
    secondsWatched: number;
    missingSubscriptionText?: string;
    latestWatched: boolean;
    providerKey: string;
    providerId: number;
    versions: {
      airTimeStart?: number;
      readableAirTimeOrRecordingTime?: string;
      resolution: string;
      eventId?: number;
      sid?: number;
      price: number;
      isRented: boolean;
      language: string;
      subtitles: string;
      secondsWatched: number;
      unPublishDate?: number;
      npvrInfo?: {
        notSeen: boolean;
        expireAt: number;
        recordedAt: number;
        npvrTrickmodes: number;
        customerRecording: boolean;
        isSeriesRecording: boolean;
        recordingStatus: string;
        enoughSpaceForRecording: boolean;
        isNpvrExtendAllowed: boolean;
        extendNpvrEventWithInSec: number;
        npvrMediaId: number;
        expireSoon: boolean;
      };
      rentalExpire?: number;
      latestWatched: boolean;
      shouldShowContinuePrompt: boolean;
      mediaId?: number;
    }[];
  }[];
  episodeTitle: string;
  shortDescription: string;
  longDescription: string;
  episodeNumber?: number;
  seriesId?: number;
  seasonId?: number;
  seasonNumber?: number;
  cast?: {
    personId: number;
    firstName: string;
    lastName: string;
    roleName: string;
    job: string;
  }[];
  lastWatched?: {
    persistantTimeIndex: number;
    highestTimeIndex: number;
    lastUpdated: number;
    secondsWatched: number;
    programLength: number;
    progressBar?: string;
    mediaId?: number;
    eventId?: number;
    sid?: number;
  };
  episodeId?: number;
  contentType?: string;
  cover?: string;
  readableAirTimeOrRecordingTime?: string;
  hasProviderAccess: boolean;
  providerAccessNeededText?: string;
  isContentRecorded: boolean;
}

const toArray = <T>(list?: KtList<T> | null): T[] =>
  list ? Array.from(list.asJsReadonlyArrayView()) : [];

const mapNpvrInfo = (npvrInfo?: NpvrInfo | null) => {
  if (!npvrInfo) return undefined;

  return {
    notSeen: Boolean(npvrInfo.isNotSeen),
    expireAt: npvrInfo.expireTimeEpochSeconds ?? 0,
    recordedAt: npvrInfo.recordedTimeEpochSeconds ?? 0,
    npvrTrickmodes: npvrInfo.trickModes?.bitmask ?? 0,
    customerRecording: Boolean(npvrInfo.isCustomerRecording),
    isSeriesRecording: Boolean(npvrInfo.isSeriesRecording),
    recordingStatus: npvrInfo.recordingStatus ?? '',
    enoughSpaceForRecording: Boolean(npvrInfo.isEnoughSpaceForRecording),
    isNpvrExtendAllowed: Boolean(npvrInfo.isNpvrExtendAllowed),
    extendNpvrEventWithInSec: npvrInfo.extendNpvrEventWithinSeconds ?? 0,
    npvrMediaId: npvrInfo.mediaId ?? 0,
    expireSoon: Boolean(npvrInfo.expiresSoon)
  };
};

const mapLastWatched = (lastWatched?: LastWatched | null) => {
  if (!lastWatched) return undefined;

  return {
    persistantTimeIndex: lastWatched.persistentTimeIndex,
    highestTimeIndex: lastWatched.highestTimeIndex,
    lastUpdated: lastWatched.lastUpdatedEpochSeconds,
    secondsWatched: lastWatched.secondsWatched,
    programLength: lastWatched.programLength,
    progressBar: lastWatched.progressBar?.name,
    mediaId: lastWatched.mediaId ?? undefined,
    eventId: lastWatched.eventId ?? undefined,
    sid: lastWatched.sid ?? undefined
  };
};

const mapVersion = (version: Version) => ({
  resolution: version.resolution,
  readableAirTimeOrRecordingTime: version.readableAirTimeOrRecordingTime ?? undefined,
  eventId: version.eventId ?? undefined,
  sid: version.sid ?? undefined,
  price: version.price,
  isRented: version.isRented,
  language: version.language,
  subtitles: version.subtitles,
  secondsWatched: version.secondsWatched,
  unPublishDate: version.unPublishDateEpochSeconds ?? undefined,
  airTimeStart: version.airTimeStartEpochSeconds ?? undefined,
  npvrInfo: mapNpvrInfo(version.npvrInfo),
  rentalExpire: version.rentalExpireEpochSeconds ?? undefined,
  latestWatched: version.latestWatched,
  shouldShowContinuePrompt: version.shouldShowContinuePrompt,
  mediaId: version.mediaId ?? undefined
});

const mapProvider = (provider: VersionProvider) => ({
  providerType: provider.providerType,
  providerName: provider.providerName,
  length: provider.length,
  covers: {
    current: provider.covers?.current ?? undefined
  },
  hasTrailer: provider.hasTrailer,
  rentalPeriod: provider.rentalPeriod,
  unPublishDate: provider.unPublishDateEpochSeconds,
  access: provider.access,
  previewDuration: provider.previewDuration ?? undefined,
  trailerMediaId: provider.trailerMediaId ?? undefined,
  lastWatched: mapLastWatched(provider.lastWatched),
  secondsWatched: provider.secondsWatched,
  missingSubscriptionText: provider.missingSubscriptionText ?? undefined,
  latestWatched: provider.latestWatched,
  providerKey: provider.providerKey,
  providerId: provider.providerId,
  versions: toArray(provider.versions).map(mapVersion)
});

const mapCast = (cast?: Cast | null) => {
  const castMembers = toArray(cast?.castMembers);
  if (!castMembers.length) return undefined;

  return castMembers.map((member) => ({
    personId: member.id,
    firstName: member.firstName ?? '',
    lastName: member.lastName ?? '',
    roleName: member.roleName ?? '',
    job: member.job ?? ''
  }));
};

function mapToContentInfoEpisodes(response: ContentInfoEpisodes): ContentInfoEpisodesRaw {
  const episodes = response.episodes?.asJsReadonlyArrayView();
  return {
    totalEpisodes: response.totalEpisodes,
    episodes: episodes
      ? episodes?.map((episode) => ({
          providers: toArray(episode.providers).map(mapProvider),
          cast: mapCast(episode.cast),
          lastWatched: mapLastWatched(episode.lastWatched),
          episodeTitle: episode.episodeTitle,
          shortDescription: episode.shortDescription,
          longDescription: episode.longDescription,
          episodeNumber: episode.episodeNumber ?? undefined,
          seriesId: episode.seriesId ?? undefined,
          seasonId: episode.seasonId ?? undefined,
          seasonNumber: episode.seasonNumber ?? undefined,
          episodeId: episode.episodeId ?? undefined,
          contentType: episode.contentType?.name ?? undefined,
          cover: episode.cover ?? undefined,
          readableAirTimeOrRecordingTime: episode.readableAirTimeOrRecordingTime ?? undefined,
          providerAccessNeededText: episode.providerAccessNeededText ?? undefined,
          hasProviderAccess: episode.hasProviderAccess,
          isContentRecorded: episode.isContentRecorded ?? false
        }))
      : []
  };
}

export async function getContentInfoEpisodes(
  seasonIds: (number | number[])[],
  start: number,
  limit: number,
  contentId: number,
  universeId?: number,
  providerCollectionId?: number,
  subProviderId?: string,
  providers?: ContentListProviders
): Promise<ContentInfoEpisodesRaw> {
  // Tizen 5.0 is running chromium 63, which does not support .flat(). Guard against malformed/empty IDs before calling KMM.
  const flattenedSeasonIds = seasonIds.reduce<number[]>((acc, seasonId) => {
    if (Array.isArray(seasonId)) {
      return acc.concat(seasonId.filter((id): id is number => typeof id === 'number' && Number.isFinite(id)));
    }

    if (typeof seasonId === 'number' && Number.isFinite(seasonId)) {
      acc.push(seasonId);
    }

    return acc;
  }, []);

  const response = await kmm((repo) =>
    repo.getContentInfoEpisodes(
      contentId,
      universeId,
      providers ? toProviderIdentifier(providers) : undefined,
      subProviderId ? subProviderId : undefined,
      providerCollectionId,
      ContentInfoEpisodes.SortingMethod.NEWEST_FIRST,
      KtList.fromJsArray<number>(flattenedSeasonIds),
      start,
      limit
    )
  );

  return mapToContentInfoEpisodes(response);
}

export default getContentInfoEpisodes;
